import { ATLAS_LIMITS, MAP_IMAGE_TYPES, imageTypeOf } from '@realmery/realm-core';
import { ImageFileError } from '../editor/imageFiles.ts';

/**
 * Getting a map image ready to import (docs/PARITY-STEP-4-PLAN.md decision 5): PNG, JPEG or
 * WebP, told by its bytes and decoded by the browser first, kept exactly as it was, up to
 * 50 MB, with the gallery's thumbnail made as the desktop's `ImageDecoding.thumbnailPng`
 * makes it: a PNG scaled down (never up) to fit 320 by 200.
 */

export interface MapImageTools {
  decode(blob: Blob): Promise<ImageBitmap>;
  /** The picture scaled into a PNG of this size. */
  toPng(bitmap: ImageBitmap, width: number, height: number): Promise<Uint8Array>;
}

export const browserMapTools: MapImageTools = {
  decode: (blob) => createImageBitmap(blob),
  async toPng(bitmap, width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const g = canvas.getContext('2d')!;
    g.imageSmoothingQuality = 'high';
    g.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new ImageFileError('The thumbnail could not be made.');
    return new Uint8Array(await blob.arrayBuffer());
  },
};

/** The thumbnail's size for a picture this size (`thumbnailPng`). */
export function thumbnailSize(width: number, height: number): { width: number; height: number } {
  const scale = Math.min(1, Math.min(ATLAS_LIMITS.thumbnailWidth / width, ATLAS_LIMITS.thumbnailHeight / height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export async function thumbnailOf(data: Uint8Array, tools: MapImageTools = browserMapTools): Promise<Uint8Array> {
  const bitmap = await tools.decode(new Blob([data as Uint8Array<ArrayBuffer>], { type: imageTypeOf(data) ?? 'image/png' }));
  try {
    const size = thumbnailSize(bitmap.width, bitmap.height);
    return await tools.toPng(bitmap, size.width, size.height);
  } finally {
    bitmap.close();
  }
}

export async function prepareMapImage(file: Pick<File, 'arrayBuffer'>, tools: MapImageTools = browserMapTools): Promise<{ data: Uint8Array; thumbnail: Uint8Array }> {
  const data = new Uint8Array(await file.arrayBuffer());
  const type = imageTypeOf(data);
  if (!type || !MAP_IMAGE_TYPES.has(type)) throw new ImageFileError("That isn't a map image Realmery can use (PNG, JPEG or WebP).");
  if (data.byteLength > ATLAS_LIMITS.imageBytes) throw new ImageFileError('A map image can be at most 50 MB.');
  try {
    return { data, thumbnail: await thumbnailOf(data, tools) };
  } catch (e) {
    if (e instanceof ImageFileError) throw e;
    throw new ImageFileError('That picture could not be read. It may be damaged.');
  }
}
